import React from 'react';
import { Plus, Edit, Trash2, Search, FileText } from 'lucide-react';
import { useCrud, useModal, useForm } from '../hooks';
import { planoContaService } from '../services/api';

const PlanosContas = () => {
  const { data: planos, loading, error, create, update, remove } = useCrud(planoContaService, 'planos de contas');
  const modal = useModal();
  const [searchTerm, setSearchTerm] = React.useState('');

  const validationRules = {
    nome: { required: true, label: 'Nome', minLength: 2, maxLength: 100 },
    tipo: { required: true, label: 'Tipo' }
  };

  const form = useForm(
    { nome: '', tipo: '' },
    validationRules
  );

  const filteredPlanos = planos.filter(plano =>
    plano.nome?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCreate = () => {
    form.reset();
    modal.open({ mode: 'create', title: 'Novo Plano de Conta' });
  };

  const handleEdit = (plano) => {
    form.reset({
      nome: plano.nome || '',
      tipo: plano.tipo || ''
    });
    modal.open({ mode: 'edit', title: 'Editar Plano de Conta', id: plano.id });
  };

  const handleDelete = async (plano) => {
    if (window.confirm(`Deseja realmente excluir o plano de conta "${plano.nome}"?`)) {
      const result = await remove(plano.id);
      if (result.success) {
        // Success handled by the hook
      }
    }
  };

  const handleSubmit = async (values) => {
    const result = modal.data.mode === 'create' 
      ? await create(values)
      : await update(modal.data.id, values);

    if (result.success) {
      modal.close();
      form.reset();
    }
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Planos de Contas</h1>
        <button onClick={handleCreate} className="btn btn-primary">
          <Plus size={20} />
          Novo Plano de Conta
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="card mb-6">
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Buscar planos de contas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-input pl-10"
            />
          </div>
        </div>
      </div>

      <div className="card">
        {filteredPlanos.length === 0 ? (
          <div className="empty-state">
            <FileText className="empty-state-icon" />
            <h3 className="text-lg font-medium mb-2">Nenhum plano de conta encontrado</h3>
            <p className="mb-4">
              {searchTerm ? 'Tente ajustar os filtros de busca.' : 'Comece criando um novo plano de conta.'}
            </p>
            {!searchTerm && (
              <button onClick={handleCreate} className="btn btn-primary">
                <Plus size={20} />
                Novo Plano de Conta
              </button>
            )}
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Tipo</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredPlanos.map((plano) => (
                  <tr key={plano.id}>
                    <td className="font-medium">{plano.nome}</td>
                    <td>
                      <span className={`px-2 py-1 rounded text-xs ${
                        plano.tipo === 'RECEITA' ? 'bg-green-100 text-green-800' :
                        plano.tipo === 'DESPESA' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {plano.tipo}
                      </span>
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(plano)}
                          className="btn btn-sm btn-secondary"
                          title="Editar"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(plano)}
                          className="btn btn-sm btn-danger"
                          title="Excluir"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {modal.isOpen && (
        <div className="modal-overlay" onClick={modal.close}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{modal.data.title}</h2>
              <button onClick={modal.close} className="modal-close">×</button>
            </div>

            <form onSubmit={form.handleSubmit(handleSubmit)}>
              <div className="form-group">
                <label className="form-label">Nome *</label>
                <input
                  type="text"
                  value={form.values.nome}
                  onChange={(e) => form.setValue('nome', e.target.value)}
                  onBlur={() => form.setTouched('nome')}
                  className={`form-input ${form.errors.nome && form.touched.nome ? 'border-red-500' : ''}`}
                  placeholder="Digite o nome do plano de conta"
                />
                {form.errors.nome && form.touched.nome && (
                  <div className="form-error">{form.errors.nome}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Tipo *</label>
                <select
                  value={form.values.tipo}
                  onChange={(e) => form.setValue('tipo', e.target.value)}
                  onBlur={() => form.setTouched('tipo')}
                  className={`form-select ${form.errors.tipo && form.touched.tipo ? 'border-red-500' : ''}`}
                >
                  <option value="">Selecione o tipo</option>
                  <option value="RECEITA">Receita</option>
                  <option value="DESPESA">Despesa</option>
                </select>
                {form.errors.tipo && form.touched.tipo && (
                  <div className="form-error">{form.errors.tipo}</div>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" onClick={modal.close} className="btn btn-secondary">
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  {modal.data.mode === 'create' ? 'Criar' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanosContas;